import { z } from 'zod';
/**
 * Extension contract for browser extension integration endpoints
 * All routes require API key authentication with 'automation:extension' scope
 */

// Extension task status
export const ExtensionTaskStatusSchema = z.enum(['pending', 'completed']);
export type ExtensionTaskStatus = z.infer<typeof ExtensionTaskStatusSchema>;

// Extension task type (matches DB schema)
export const ExtensionTaskSchema = z.object({
  id: z.string(),
  userId: z.string(),
  status: ExtensionTaskStatusSchema,
  action: z.string(),
  data: z.unknown(),
  result: z.unknown().nullable(),
  errorMessage: z.string().nullable(),
  createdAt: z.date(),
  startedAt: z.date().nullable(),
  completedAt: z.date().nullable(),
  updatedAt: z.date(),
});
export type ExtensionTask = z.infer<typeof ExtensionTaskSchema>;

// Poll for pending tasks - GET /api/extension/tasks/user/:userId
export const ExtensionTasksPollParamsSchema = z.object({
  userId: z.string().min(1),
});
export type ExtensionTasksPollParams = z.infer<typeof ExtensionTasksPollParamsSchema>;

// tRPC input schema (uses query param directly)
export const ExtensionTasksPollInputSchema = ExtensionTasksPollParamsSchema;
export type ExtensionTasksPollInput = z.infer<typeof ExtensionTasksPollInputSchema>;

export const ExtensionTasksPollOutputSchema = z.object({
  tasks: z.array(ExtensionTaskSchema),
});
export type ExtensionTasksPollOutput = z.infer<typeof ExtensionTasksPollOutputSchema>;

// Complete a task - POST /api/extension/tasks/:taskId/complete
export const ExtensionTaskCompleteBodySchema = z.object({
  result: z.unknown().optional(),
});
export const ExtensionTaskCompleteParamsSchema = z.object({
  taskId: z.string().min(1),
});
export type ExtensionTaskCompleteBody = z.infer<typeof ExtensionTaskCompleteBodySchema>;
export type ExtensionTaskCompleteParams = z.infer<typeof ExtensionTaskCompleteParamsSchema>;

// tRPC input schema (combines path params and body)
export const ExtensionTaskCompleteInputSchema = ExtensionTaskCompleteParamsSchema.merge(
  ExtensionTaskCompleteBodySchema
);
export type ExtensionTaskCompleteInput = z.infer<typeof ExtensionTaskCompleteInputSchema>;

export const ExtensionTaskCompleteOutputSchema = z.object({
  success: z.boolean(),
  task: ExtensionTaskSchema,
});
export type ExtensionTaskCompleteOutput = z.infer<typeof ExtensionTaskCompleteOutputSchema>;

// Timeline sync - POST /api/extension/timeline/sync
export const ExtensionTimelineSyncBodySchema = z.object({
  source: z.string().optional(),
  tweets: z.array(z.unknown()),
  metadata: z.record(z.string(), z.unknown()).optional(),
  taskId: z.string().optional(),
});
export type ExtensionTimelineSyncBody = z.infer<typeof ExtensionTimelineSyncBodySchema>;

// tRPC input schema (same as body for POST)
export const ExtensionTimelineSyncInputSchema = ExtensionTimelineSyncBodySchema;
export type ExtensionTimelineSyncInput = z.infer<typeof ExtensionTimelineSyncInputSchema>;

export const ExtensionTimelineSyncOutputSchema = z.object({
  success: z.boolean(),
  syncId: z.string(),
  tweetCount: z.number(),
});
export type ExtensionTimelineSyncOutput = z.infer<typeof ExtensionTimelineSyncOutputSchema>;
